<template>
    <!--
      BLOCO S — item 33: "Disponibilizar aos professores recursos para cadastrar medições
      antropométricas dos alunos, compartilhando com nutricionistas da rede informações
      sobre a estatura e massa muscular dos alunos."

      Toda a regra — IMC, escore-z, classificação, aplicabilidade por idade — é do backend.
      Esta tela não calcula nada: exibe o que a API devolveu e declara o que não se aplica.
    -->
    <div class="main">
        <el-alert v-if="referenciaAviso" :title="referenciaAviso" type="warning"
                  :closable="false" class="u-mb-15"></el-alert>

        <el-form label-position="top">
            <el-row :gutter="10">
                <el-col :xs="24" :sm="18">
                    <el-form-item label="Instituição" v-if="ehSmed">
                        <el-select v-model="filtro.instituicao_id" @change="getTurmas()" filterable
                                   clearable placeholder="Pesquisar Instituição" style="width: 100%;">
                            <el-option v-for="(item, i) in $store.state.instituicoes" :key="i"
                                       :label="item.nome" :value="item.id"></el-option>
                        </el-select>
                    </el-form-item>
                    <div class="style-form" v-else>
                        <label>Instituição</label>
                        <div class="style-input readonly">{{ $store.state.config.instituicao ? $store.state.config.instituicao.nome : '' }}</div>
                    </div>
                </el-col>
                <el-col :xs="24" :sm="6">
                    <el-form-item label="Período">
                        <el-select v-model="filtro.periodo_letivo_id" @change="getTurmas()" style="width: 100%;">
                            <el-option v-for="(item, i) in $store.state.periodos" :key="i"
                                       :label="'Periodo Letivo ' + item.nome" :value="item.id"></el-option>
                        </el-select>
                    </el-form-item>
                </el-col>
            </el-row>

            <el-row :gutter="10">
                <el-col :xs="24" :sm="24">
                    <el-form-item label="Turma">
                        <el-select v-model="filtro.turma_id" @change="carregarTurma()" clearable filterable
                                   placeholder="Selecione a turma" style="width: 100%;">
                            <el-option-group v-for="(grupo, i) in turmas" :key="i" :label="i">
                                <el-option v-for="t in grupo" :key="t.id"
                                           :label="`${t.curso ? t.curso.nome : ''} ${t.nome}`" :value="t.id"></el-option>
                            </el-option-group>
                        </el-select>
                    </el-form-item>
                </el-col>
            </el-row>
        </el-form>

        <el-alert v-if="erro" :title="erro" type="error" :closable="false" class="u-mb-15"></el-alert>

        <div v-if="carregando" class="u-text-center u-mt-20">Carregando...</div>

        <template v-else-if="turma.alunos">
            <p class="u-mb-10">
                <b>{{ turma.turma ? turma.turma.nome : '' }}</b> —
                {{ turma.total }} aluno(s), {{ turma.sem_medicao }} sem medição registrada.
            </p>

            <el-table :data="turma.alunos" border size="mini" empty-text="Nenhum aluno matriculado nesta turma.">
                <el-table-column prop="nome" label="Aluno" min-width="200"></el-table-column>

                <el-table-column label="Última medição" width="120">
                    <template slot-scope="s">
                        <span v-if="!s.row.possui_medicao" class="vazio">Sem medição</span>
                        <span v-else>{{ formatarData(s.row.ultima_medicao.data_medicao) }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="Peso" width="90">
                    <template slot-scope="s">
                        <span v-if="!s.row.possui_medicao || s.row.ultima_medicao.peso_kg === null" class="vazio">—</span>
                        <span v-else>{{ s.row.ultima_medicao.peso_kg }} kg</span>
                    </template>
                </el-table-column>

                <el-table-column label="Altura" width="90">
                    <template slot-scope="s">
                        <span v-if="!s.row.possui_medicao || s.row.ultima_medicao.altura_cm === null" class="vazio">—</span>
                        <span v-else>{{ s.row.ultima_medicao.altura_cm }} cm</span>
                    </template>
                </el-table-column>

                <el-table-column label="IMC" width="80">
                    <template slot-scope="s">
                        <span v-if="!s.row.possui_medicao || s.row.ultima_medicao.imc === null" class="vazio">—</span>
                        <span v-else>{{ s.row.ultima_medicao.imc }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="Classificação (OMS/SISVAN)" min-width="240">
                    <template slot-scope="s">
                        <span v-if="!s.row.possui_medicao" class="vazio">—</span>
                        <template v-else>
                            <!-- Indicador não calculado NÃO vira eutrofia: aparece como não aplicável. -->
                            <el-tag v-for="(v, k) in s.row.ultima_medicao.indicadores" :key="k"
                                    size="mini" class="u-mr-5">
                                {{ rotuloIndicador(k) }}: {{ v.classificacao }} (z {{ v.escore_z }})
                            </el-tag>
                            <span v-if="!temIndicadores(s.row.ultima_medicao)" class="vazio">
                                Sem indicador aplicável
                            </span>
                        </template>
                    </template>
                </el-table-column>

                <el-table-column label="" width="200">
                    <template slot-scope="s">
                        <el-button size="mini" @click="abrirMedicao(s.row)">MEDIR</el-button>
                        <el-button class="u-ml-10" size="mini" plain @click="abrirHistorico(s.row)">HISTÓRICO</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </template>

        <!-- Registro de medição -->
        <el-dialog :title="`Nova medição — ${alunoSelecionado.nome || ''}`" :visible.sync="dialogMedicao" width="460px">
            <el-form label-position="top">
                <el-row :gutter="10">
                    <el-col :span="12">
                        <el-form-item label="Peso (kg)">
                            <el-input v-model="form.peso_kg" type="number" step="0.1"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="Altura (cm)">
                            <el-input v-model="form.altura_cm" type="number" step="0.1"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="Data da medição">
                    <el-date-picker v-model="form.data_medicao" type="date" format="dd/MM/yyyy"
                                    value-format="yyyy-MM-dd" style="width: 100%;"></el-date-picker>
                </el-form-item>

                <el-form-item label="Observação (opcional)">
                    <el-input v-model="form.observacao" type="textarea" :rows="2" maxlength="1000"></el-input>
                </el-form-item>

                <el-alert v-if="erroMedicao" :title="erroMedicao" type="error" :closable="false"></el-alert>
            </el-form>

            <span slot="footer">
                <el-button @click="dialogMedicao = false">CANCELAR</el-button>
                <el-button class="u-ml-10" type="primary" :loading="salvando" @click="salvarMedicao()">SALVAR</el-button>
            </span>
        </el-dialog>

        <!-- Histórico -->
        <el-dialog :title="`Histórico — ${alunoSelecionado.nome || ''}`" :visible.sync="dialogHistorico" width="820px">
            <p v-if="historico.referencia" class="referencia">
                Referência: {{ historico.referencia.fonte }} — {{ historico.referencia.versao }}
            </p>

            <el-table :data="historico.medicoes" border size="mini"
                      empty-text="Nenhuma medição registrada para este aluno.">
                <el-table-column label="Data" width="100">
                    <template slot-scope="s">{{ formatarData(s.row.data_medicao) }}</template>
                </el-table-column>
                <el-table-column label="Idade" width="90">
                    <template slot-scope="s">
                        <span v-if="s.row.idade_meses === null" class="vazio">—</span>
                        <span v-else>{{ idadeTexto(s.row.idade_meses) }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="peso_kg" label="Peso" width="70"></el-table-column>
                <el-table-column prop="altura_cm" label="Altura" width="70"></el-table-column>
                <el-table-column prop="imc" label="IMC" width="70"></el-table-column>
                <el-table-column label="Indicadores" min-width="300">
                    <template slot-scope="s">
                        <div v-for="(v, k) in s.row.indicadores" :key="k">
                            <b>{{ rotuloIndicador(k) }}:</b> {{ v.classificacao }}
                            <span class="escore">(escore-z {{ v.escore_z }} · {{ v.origem_documento }})</span>
                        </div>
                        <div v-for="(n, i) in s.row.indicadores_nao_aplicaveis" :key="'n' + i" class="nao-aplicavel">
                            {{ rotuloIndicador(n.indicador) }}: {{ n.motivo }}
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="" width="80">
                    <template slot-scope="s">
                        <el-button size="mini" type="danger" @click="excluirMedicao(s.row)">EXCLUIR</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>
    </div>
</template>

<script>
const ROTULOS = {
    imc_idade: 'IMC por idade',
    estatura_idade: 'Estatura por idade',
    peso_idade: 'Peso por idade',
    peso_estatura: 'Peso por estatura',
    peso_comprimento: 'Peso por comprimento',
    todos: 'Todos os indicadores'
}

export default {
    data: () => ({
        turmas: [],
        turma: {},
        historico: {},
        alunoSelecionado: {},
        filtro: { instituicao_id: '', periodo_letivo_id: '', turma_id: '' },
        form: { peso_kg: '', altura_cm: '', data_medicao: '', observacao: '' },
        dialogMedicao: false,
        dialogHistorico: false,
        carregando: false,
        salvando: false,
        erro: '',
        erroMedicao: '',
        referenciaAviso: ''
    }),
    computed: {
        /**
         * O tipo do usuário mora em `state.user`, não em `state.config` — `config` guarda
         * escola e período correntes, e NUNCA teve a chave `usuario`. Lendo o caminho
         * errado, `t` era sempre null e `ehSmed` sempre false: o select de instituição não
         * era renderizado, `filtro.instituicao_id` ficava vazio para perfis de rede (que
         * não têm escola no contexto), a lista de turmas nunca carregava e os botões
         * dependentes de `podeGerar` ficavam desabilitados para sempre.
         *
         * Perfis de abrangência de rede: 4, 5, 6, 7, 8 e 10.
         */
        ehSmed () {
            const t = parseInt(this.$store.state.user ? this.$store.state.user.tipo : null)
            return t === 10 || (t > 3 && t <= 8)
        }
    },
    mounted () {
        this.filtro.periodo_letivo_id = this.$store.state.config.parametros.periodo_letivo_situacao_academica

        if (!this.ehSmed && this.$store.state.config.instituicao) {
            this.filtro.instituicao_id = this.$store.state.config.instituicao.id
        }

        this.getReferencia()
        this.getTurmas()
    },
    methods: {
        /** A tela avisa quando a referência não está instalada, em vez de mostrar campos vazios. */
        getReferencia () {
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/antropometria/referencia`,
                            this.$store.getters.getHeaderAuth).then(r => {
                const d = r.data || {}
                if (d.erro || d.error) { this.referenciaAviso = d.erro || d.error; return }
                this.referenciaAviso = d.instalada ? '' : d.aviso
            }).catch(e => this.tratarFalha(e))
        },

        getTurmas () {
            this.filtro.turma_id = ''
            this.turma = {}

            if (!this.filtro.instituicao_id || !this.filtro.periodo_letivo_id) { this.turmas = []; return }

            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/turma/getTurmasForListDiario/${this.filtro.instituicao_id}/${this.filtro.periodo_letivo_id}`,
                            this.$store.getters.getHeaderAuth).then(r => {
                const d = r.data || {}
                if (d.erro || d.error) { this.erro = d.erro || d.error; return }
                this.turmas = d
            }).catch(e => this.tratarFalha(e))
        },

        carregarTurma () {
            this.turma = {}
            this.erro = ''

            if (!this.filtro.turma_id) return

            this.carregando = true

            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/antropometria/turma/${this.filtro.turma_id}`,
                            this.$store.getters.getHeaderAuth).then(r => {
                this.carregando = false
                const d = r.data || {}
                if (d.erro || d.error) { this.erro = d.erro || d.error; return }
                this.turma = d
            }).catch(e => { this.carregando = false; this.tratarFalha(e) })
        },

        abrirMedicao (linha) {
            this.alunoSelecionado = linha
            this.erroMedicao = ''
            this.form = { peso_kg: '', altura_cm: '',
                          data_medicao: new Date().toISOString().substring(0, 10), observacao: '' }
            this.dialogMedicao = true
        },

        salvarMedicao () {
            this.erroMedicao = ''
            this.salvando = true

            const payload = Object.assign({ aluno_id: this.alunoSelecionado.aluno_id }, this.form)

            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/antropometria/registrar`,
                             payload, this.$store.getters.getHeaderAuth).then(r => {
                this.salvando = false
                const d = r.data || {}

                // Só anuncia sucesso depois de ler a resposta.
                if (d.erro || d.error) { this.erroMedicao = d.erro || d.error; return }

                this.dialogMedicao = false
                this.$message.success(d.mensagem || 'Medição registrada.')

                // Avisos do backend (ex.: referência não instalada) são exibidos, não engolidos.
                if (d.avisos && d.avisos.length) {
                    d.avisos.forEach(a => this.$message.warning(a))
                }

                this.carregarTurma()
            }).catch(e => { this.salvando = false; this.tratarFalha(e, 'erroMedicao') })
        },

        abrirHistorico (linha) {
            this.alunoSelecionado = linha
            this.historico = {}

            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/antropometria/historico/${linha.aluno_id}`,
                            this.$store.getters.getHeaderAuth).then(r => {
                const d = r.data || {}
                if (d.erro || d.error) { this.$message.error(d.erro || d.error); return }
                this.historico = d
                this.dialogHistorico = true
            }).catch(e => this.tratarFalha(e))
        },

        excluirMedicao (linha) {
            this.$confirm('Excluir esta medição?', 'Confirmação', { type: 'warning' }).then(() => {
                this.$axios.delete(`${this.$store.state.config.baseUrlApi}/api/antropometria/excluir/${linha.id}`,
                                   this.$store.getters.getHeaderAuth).then(r => {
                    const d = r.data || {}
                    if (d.erro || d.error) { this.$message.error(d.erro || d.error); return }
                    this.$message.success(d.mensagem || 'Medição excluída.')
                    this.abrirHistorico(this.alunoSelecionado)
                    this.carregarTurma()
                }).catch(e => this.tratarFalha(e))
            }).catch(() => {})
        },

        temIndicadores (medicao) {
            return medicao && medicao.indicadores && Object.keys(medicao.indicadores).length > 0
        },

        rotuloIndicador (chave) { return ROTULOS[chave] || chave },

        idadeTexto (meses) {
            const a = Math.floor(meses / 12)
            const m = meses % 12
            return m ? `${a}a ${m}m` : `${a}a`
        },

        formatarData (d) {
            if (!d) return '—'
            const p = String(d).substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        },

        tratarFalha (e, campo) {
            const d = (e && e.response && e.response.data) ? e.response.data : {}
            const msg = d.erro || d.error || 'Não foi possível concluir a operação.'
            if (campo) this[campo] = msg
            else this.$message.error(msg)
        }
    }
}
</script>

<style scoped>
.vazio { color: #c0c4cc; }
.escore { color: #909399; font-size: 11px; }
.nao-aplicavel { color: #b88230; font-size: 11px; }
.referencia { font-size: 12px; color: #909399; margin-bottom: 10px; }
</style>
